/**
 * Sewing Efficiency — which side of its target a figure fell, and how that is drawn.
 *
 * One definition, used by the headline cards and by the Top 3 / Last 3 panels, so the page cannot
 * say "under target" two different ways or in two different colours. It is **report-local and
 * stays that way**: §4.6 keeps `<StatusBadge>` and the application-wide status vocabulary unbuilt
 * until the business settles it, and nothing here is a candidate for promotion — there is no
 * status name, no fixed set of states, and no badge.
 *
 * **The comparison is two server-computed numbers and nothing else.** No threshold is invented and
 * no value is banded — legacy's 0.60/0.40 efficiency bands stay unported, because §5.2 keeps
 * thresholds in ADB SQL and a plausible invented one is worse than none. No target means `null`,
 * which renders neutral.
 *
 * **The words and the arrow are the signal; colour is the third channel** (§4.9). `status-ok`
 * against `status-critical` measures OKLab ΔE 2.4 in dark mode under deuteranopia simulation —
 * one colour to some readers. `chart.tsx` keeps blue-against-red for the same reason: a bar has
 * nowhere to put a word.
 *
 * **Where colour may go:** `status-ok` is 3.29:1 on the light card, so it clears the 3:1 graphics
 * and large-text floor and misses 4.5:1 for body text. Colour therefore rides the large headline
 * figure and the arrows; small text stays `foreground`/`muted-foreground` with a coloured arrow.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import type { ChartColorToken } from '@/components/ui/chart';
import { cn } from '@/lib/utils';

/** The comparison, or `null` when there is nothing to compare — a real and common answer. */
export type Standing = 'at-or-over' | 'under';

/**
 * How far a figure sits from its target, signed, or `null` where there is nothing to compare — the
 * same conditions {@link standing} answers `null` for, in one place so the page cannot hold two
 * answers to one question. A distance between two server-returned numbers, not a threshold (§5.2).
 */
export function standingGap(actual: number | null, target: number | null): number | null {
  if (actual === null || target === null || target <= 0) {
    return null;
  }
  return actual - target;
}

export function standing(actual: number | null, target: number | null): Standing | null {
  const gap = standingGap(actual, target);
  if (gap === null) {
    return null;
  }
  return gap < 0 ? 'under' : 'at-or-over';
}

export const STANDING: Record<
  Standing,
  {
    icon: IconDefinition;
    /** The primary signal. Reads the same in greyscale and under any form of colour blindness. */
    words: string;
    /** For a figure at 24px or larger only — see the contrast note above. */
    figure: string;
    /** For the arrow, and for anything else that is a graphic rather than text. */
    mark: string;
    /** A 10% wash behind a row. Non-text, so it changes nothing about the contrast of what is on it. */
    wash: string;
    /** The same colour as a chart token, for a Recharts mark, which will not take a class. */
    chart: ChartColorToken;
  }
> = {
  'at-or-over': {
    icon: faArrowUp,
    words: 'At or over target',
    figure: 'text-status-ok',
    mark: 'text-status-ok',
    wash: 'bg-status-ok/10',
    chart: 'status-ok',
  },
  under: {
    icon: faArrowDown,
    words: 'Under target',
    figure: 'text-status-critical',
    mark: 'text-status-critical',
    wash: 'bg-status-critical/10',
    chart: 'status-critical',
  },
};

/**
 * The arrow that rides a figure. `aria-hidden` on purpose: every caller puts the same words within
 * reach, and an icon that duplicates adjacent text out loud is noise, not access (§4.13).
 */
export function StandingArrow({ of, className }: { of: Standing; className?: string }) {
  return (
    <FontAwesomeIcon
      icon={STANDING[of].icon}
      className={cn('shrink-0', STANDING[of].mark, className)}
      aria-hidden
    />
  );
}
